import React from "react";
import Seo from "@/components/Seo";
import {SEO} from "@/lib/seoConfig";
import {Link} from "react-router-dom";
import {Section, Kicker, SerifTitle} from "@/components/Section";
import {TestimonialCard} from "@/components/TestimonialCard";
import {SelectedClientWork} from "@/components/SelectedClientWork";
import {useTestimonials} from "@/lib/content";

// Phase 11 — /reviews simplified for trust-and-conversion (cold business
// traffic from outbound: resorts, med-spas, events, law firms, schools).
//
// Three structural changes vs Phase 10:
//   1. Mid-page CTA band REMOVED — reviews now read as one continuous
//      body of proof. MidPageCtaBand.jsx stays in the codebase for
//      potential future use elsewhere, just not rendered here.
//   2. Hero copy refined: new eyebrow ("Client experiences"), supporting
//      line names the audience explicitly (clients/founders/families/
//      artists/teams).
//   3. Targeted spacing tightening on /reviews specifically (per-section
//      spacing="tight" overrides where the Phase 10 global default still
//      reads too generous on this particular page). Other pages
//      unaffected — system Section scale is correct elsewhere.
//
// Card content + photo treatment changes live in TestimonialCard.jsx
// (Phase 11 4-line layout + faded-backdrop variant).

// ═══════════════════════════════════════════════════════════════════════
// Phase 11 final CTA copy locked (user confirmed 2026-05-27):
// ═══════════════════════════════════════════════════════════════════════
//   Headline:        "Let's work together."
//   Supporting line: "Tell me what you need photographed, and I'll send
//                     the best next step."
//   Button:          "Request Availability" → /inquire
//
// Replaces the earlier "Ready for images that feel like you and work
// for {tail}" pattern. The new copy reads as a direct invitation
// instead of a marketing tagline — better fit for the trust-and-
// conversion brief.
// ═══════════════════════════════════════════════════════════════════════

const HERO_SUPPORTING_LINE =
    "Notes from clients, founders, families, artists, and teams I have photographed.";

export default function Reviews() {
    const REVIEWS = useTestimonials("reviews");

    return (
        <div className="pt-24 sm:pt-28">
            <Seo {...SEO.reviews} />

            {/* ─── HERO ─────────────────────────────────────────────────────
              *
              * Phase 11 copy:
              *   Eyebrow:    "Client experiences"  (was "Clients")
              *   Headline:   "In their own words." (unchanged)
              *   Supporting: new line — names buyer types explicitly
              *
              * spacing="tight" keeps the hero compact so the gap into
              * the testimonials grid below feels intentional, not empty.
              */}
            <Section spacing="tight">
                <Kicker>Client experiences</Kicker>
                <SerifTitle className="mt-3">In their own words.</SerifTitle>
                <p className="mt-5 max-w-[60ch] text-sm sm:text-base leading-[1.75] text-muted-foreground">
                    {HERO_SUPPORTING_LINE}
                </p>
                <div className="mt-6 sm:mt-7 flex flex-wrap items-center gap-3">
                    <Link
                        to="/inquire"
                        data-testid="reviews-hero-primary-cta"
                        className="btn-request btn-request--sm"
                    >
                        Request Availability
                    </Link>
                    <Link
                        to="/business"
                        data-testid="reviews-hero-secondary-cta"
                        className="btn-request btn-request--sm btn-request--ghost"
                    >
                        View Business Work
                    </Link>
                </div>
            </Section>

            {/* ─── TESTIMONIALS GRID (CONTINUOUS — no mid-CTA) ──────────────
              *
              * Phase 11 — reviews read as one continuous body of proof.
              * No interruptions inside the grid (mid-page CTA band from
              * Phase 10 removed). Spacing tightened from "normal" → "tight"
              * on this section specifically so the gap above the grid
              * doesn't read empty.
              */}
            <Section spacing="tight" className="border-t border-border">
                <div
                    className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8"
                    data-testid="testimonials-section"
                >
                    {REVIEWS.map((r) => (
                        <TestimonialCard key={r.id} testimonial={r} />
                    ))}
                </div>
            </Section>

            {/* ─── SELECTED CLIENT WORK (compact, position from Phase 10) ───
              *
              * Position unchanged from Phase 10 (below grid). Variant
              * stays "compact" — Phase 11 confirms this is correct.
              * Section.spacing is the strip's internal default; bordered
              * keeps the brass hairline above it for separation from the
              * testimonials grid.
              */}
            <SelectedClientWork
                spacing="tight"
                bordered={true}
                variant="compact"
                testId="reviews-selected-clients"
            />

            {/* ─── FINAL CTA ────────────────────────────────────────────────
              *
              * Phase 11 (final, post-user-sign-off):
              *   Headline:   "Let's work together."
              *   Supporting: "Tell me what you need photographed, and I'll
              *                send the best next step."
              *   Button:     "Request Availability" → /inquire
              *
              * Replaces the placeholder "Ready for images…" pattern.
              * Reads as a direct invitation, not a marketing tagline.
              * spacing="tight" — closes the page compactly; porcelain
              * background gives visual separation from the preceding strip.
              */}
            <Section
                spacing="tight"
                className="text-center bg-[hsl(var(--lux-porcelain))] border-t border-border"
            >
                <SerifTitle className="max-w-[24ch] mx-auto">
                    Let&apos;s work together.
                </SerifTitle>
                <p
                    className="mt-4 text-sm sm:text-base leading-[1.75] text-muted-foreground max-w-[52ch] mx-auto"
                    data-testid="reviews-final-cta-supporting"
                >
                    Tell me what you need photographed, and I&apos;ll send the
                    best next step.
                </p>
                <Link
                    to="/inquire"
                    className="btn-request mt-6 inline-flex"
                    data-testid="reviews-final-cta"
                >
                    Request Availability
                </Link>
            </Section>
        </div>
    );
}
